/* ==========================================================================
   pdp-wave-carousel.js — <wave-carousel> + preorder wave add-to-cart

   Three jobs:
   1. <wave-carousel>: prev/next arrows over a native scroll-snap row.
   2. Wave ATC ([data-wave-add]): add ONE figure with its Downpay selling plan.
   3. Add-all ([data-wave-add-all]): add every addable figure in ONE request,
      each line carrying the deposit plan.

   We do the adds ourselves (not via the theme's is="product-form") because
   Downpay's page script claims product forms and resets their selling_plan,
   which was dropping the deposit plan from wave adds. Variant + plan ids come
   from data-* attrs (Downpay can't touch those); we drive the theme's own cart
   drawer via its cartUpdate pubsub (falling back to the cart:refresh event).
   ========================================================================== */
(function () {
  if (customElements.get('wave-carousel')) return;

  /* ---- carousel arrows ------------------------------------------------- */
  class WaveCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-wave-track]');
      this.prevBtn = this.querySelector('[data-wave-prev]');
      this.nextBtn = this.querySelector('[data-wave-next]');
      if (!this.track) return;

      this._update = this.update.bind(this);
      if (this.prevBtn) this.prevBtn.addEventListener('click', function () { this.page(-1); }.bind(this));
      if (this.nextBtn) this.nextBtn.addEventListener('click', function () { this.page(1); }.bind(this));
      this.track.addEventListener('scroll', this._update, { passive: true });
      window.addEventListener('resize', this._update, { passive: true });
      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._update);
    }

    step() {
      var card = this.track.querySelector('.pdp-wave__card');
      var gap = 16;
      var w = card ? card.getBoundingClientRect().width + gap : 240;
      var perView = Math.max(1, Math.floor(this.track.clientWidth / w));
      return w * perView;
    }

    page(dir) {
      this.track.scrollBy({ left: dir * this.step(), behavior: 'smooth' });
    }

    update() {
      var t = this.track;
      var overflow = t.scrollWidth > t.clientWidth + 4;
      var atStart = t.scrollLeft <= 2;
      var atEnd = t.scrollLeft >= (t.scrollWidth - t.clientWidth - 2);
      if (this.prevBtn) this.prevBtn.hidden = !overflow || atStart;
      if (this.nextBtn) this.nextBtn.hidden = !overflow || atEnd;
    }
  }

  customElements.define('wave-carousel', WaveCarousel);

  /* ---- shared cart helpers -------------------------------------------- */
  function routes() { return (window.theme && theme.routes) || {}; }

  function collectSections() {
    // Lets the cart drawer register its section so /cart/add returns its HTML.
    var sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sections } }));
    return sections;
  }

  function setBusy(btn, labelSel, busyText) {
    var labelEl = btn.querySelector(labelSel);
    btn._waveLabelEl = labelEl;
    btn._waveOriginal = labelEl ? labelEl.textContent : '';
    btn.setAttribute('aria-disabled', 'true');
    btn.setAttribute('aria-busy', 'true');
    if (labelEl) labelEl.textContent = busyText;
  }

  function clearBusy(btn) {
    btn.removeAttribute('aria-disabled');
    btn.removeAttribute('aria-busy');
    if (btn._waveLabelEl) btn._waveLabelEl.textContent = btn._waveOriginal;
  }

  // Re-render the cart drawer + count off a fresh cart, then open it.
  function afterAdd(state, trigger) {
    var cartUrl = routes().cart_url || '/cart';
    return fetch(cartUrl + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        cart.sections = state.sections;
        var published = false;
        try {
          if (window.theme && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS) {
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'wave-carousel', cart: cart });
            published = true;
          }
        } catch (e) { /* fall through to cart:refresh */ }

        var drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.show === 'function') {
          if (!published) document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
          drawer.show(trigger);
        } else {
          document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
        }
      });
  }

  function post(url, body, isJson) {
    var headers = { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/javascript' };
    if (isJson) headers['Content-Type'] = 'application/json';
    return fetch(url, { method: 'POST', headers: headers, body: body })
      .then(function (r) { return r.json(); })
      .then(function (state) {
        if (state.status) { throw new Error(state.description || 'Add to cart failed'); }
        return state;
      });
  }

  /* ---- add one -------------------------------------------------------- */
  function addWaveItem(btn) {
    if (btn.getAttribute('aria-disabled') === 'true') return;
    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;
    var plan = btn.getAttribute('data-selling-plan');

    setBusy(btn, '[data-wave-add-label]', 'Adding…');

    var sections = collectSections();
    var body = new FormData();
    body.append('id', variantId);
    body.append('quantity', '1');
    if (plan) body.append('selling_plan', plan);
    if (sections.length) { body.append('sections', sections.join(',')); body.append('sections_url', window.location.pathname); }

    post(routes().cart_add_url || '/cart/add', body, false)
      .then(function (state) { return afterAdd(state, btn); })
      .catch(function () { document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } })); })
      .then(function () { clearBusy(btn); });
  }

  /* ---- add the whole wave (one request) ------------------------------- */
  function addWaveAll(btn) {
    if (btn.getAttribute('aria-disabled') === 'true') return;
    var root = btn.closest('.pdp-wave') || document;
    var items = Array.prototype.slice.call(root.querySelectorAll('[data-wave-add]'))
      .map(function (b) {
        var id = b.getAttribute('data-variant-id');
        if (!id) return null;
        var item = { id: id, quantity: 1 };
        var plan = b.getAttribute('data-selling-plan');
        if (plan) item.selling_plan = plan;
        return item;
      })
      .filter(Boolean);
    if (!items.length) return;

    setBusy(btn, '[data-wave-addall-label]', 'Adding the wave…');

    var payload = { items: items };
    var sections = collectSections();
    if (sections.length) { payload.sections = sections.join(','); payload.sections_url = window.location.pathname; }

    post(routes().cart_add_url || '/cart/add', JSON.stringify(payload), true)
      .then(function (state) { return afterAdd(state, btn); })
      .catch(function () { document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } })); })
      .then(function () { clearBusy(btn); });
  }

  // Capture phase so we win before any Downpay-attached bubble handler.
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var all = e.target.closest('[data-wave-add-all]');
    if (all) { e.preventDefault(); e.stopImmediatePropagation(); addWaveAll(all); return; }
    var one = e.target.closest('[data-wave-add]');
    if (one) { e.preventDefault(); e.stopImmediatePropagation(); addWaveItem(one); }
  }, true);
})();
